// src/Dashboard/pages/user/Galleries/AlbumGalerieDashboard.jsx

import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";

import { useAlbumGalerie } from "./hooks/useAlbumGalerie";

import AlbumHeader from "./components/AlbumHeader";
import AlbumKpis from "./components/AlbumKpis";
import PhotoCard from "./components/PhotoCard";
import PhotoSkeleton from "./components/PhotoSkeleton";
import PhotoDrawer from "./components/PhotoDrawer";
import RemovePhotoDrawer from "./components/RemovePhotoDrawer";
import PhotoLightbox from "./components/PhotoLightbox";
import GalleriesEmptyState from "./components/GalleriesEmptyState";

export default function AlbumGalerieDashboard() {
  useScrollToTop();

  const {
    navigate,
    gallery,
    loading,
    photos,
    visiblePhotos,
    stats,
    isManager,

    loadMore,

    photoDrawerOpen,
    photoDrawerMode,
    selectedPhoto,
    openCreatePhotoDrawer,
    openEditPhotoDrawer,
    closePhotoDrawer,
    handlePhotoSaved,

    removeDrawerOpen,
    openRemoveDrawer,
    closeRemoveDrawer,
    handlePhotoDeleted,

    lightboxOpen,
    lightboxIndex,
    openLightbox,
    closeLightbox,

    draggingIndex,
    overIndex,
    onDragStart,
    onDragOver,
    onDragLeave,
    onDrop,

    handleToggleCover,
    copyShare,
    refresh,
  } = useAlbumGalerie();

  const [purify, setPurify] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const mod = await import("dompurify");
        setPurify(() => (mod.default ? mod.default : mod));
      } catch {}
    })();
  }, []);

  const albumDescSafe = useMemo(() => {
    const raw = gallery?.description || "";

    try {
      return purify ? purify.sanitize(raw) : raw;
    } catch {
      return raw;
    }
  }, [gallery?.description, purify]);

  if (!loading && !gallery) {
    return (
      <section className="w-full pb-10 pt-5 font-satoshi">
        <div className="mx-auto max-w-[1400px] px-3 sm:px-4 lg:px-6">
          <GalleriesEmptyState
            message="Album introuvable"
            description="L’album demandé n’existe pas ou n’est plus disponible."
            onReset={() => navigate(-1)}
            resetLabel="Retour"
          />
        </div>
      </section>
    );
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] space-y-5 px-3 sm:px-4 lg:px-6">
        <AlbumHeader
          gallery={gallery}
          loading={loading}
          photosCount={photos.length}
          isManager={isManager}
          onBack={() => navigate(-1)}
          onRefresh={refresh}
          onShare={copyShare}
          onAdd={openCreatePhotoDrawer}
          onRemove={openRemoveDrawer}
        />

        <AlbumKpis stats={stats} loading={loading} isManager={isManager} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            {gallery?.description && (
              <div className="mb-5 rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  Description de l’album
                </p>

                <div
                  className="prose prose-sm max-w-none text-slate-700 dark:prose-invert dark:text-slate-200 [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1"
                  dangerouslySetInnerHTML={{ __html: albumDescSafe }}
                />
              </div>
            )}

            {loading ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 9 }).map((_, index) => (
                  <PhotoSkeleton key={index} />
                ))}
              </div>
            ) : photos.length === 0 ? (
              <GalleriesEmptyState
                message="Aucune photo pour l’instant"
                description="Ajoutez une première photo pour alimenter cet album."
                isManager={isManager}
                onCreate={openCreatePhotoDrawer}
                createLabel="Ajouter une photo"
              />
            ) : (
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {visiblePhotos.map((photo, index) => (
                    <PhotoCard
                      key={photo.id || index}
                      photo={photo}
                      index={index}
                      isManager={isManager}
                      dragging={draggingIndex === index}
                      over={overIndex === index}
                      onOpen={() => openLightbox(index)}
                      onEdit={() => openEditPhotoDrawer(photo)}
                      onToggleCover={(event) =>
                        handleToggleCover(event, photo)
                      }
                      onDragStart={(event) => onDragStart(event, index)}
                      onDragOver={(event) => onDragOver(event, index)}
                      onDragLeave={onDragLeave}
                      onDrop={(event) => onDrop(event, index)}
                    />
                  ))}

                  {isManager && (
                    <>
                      <button
                        type="button"
                        onClick={openCreatePhotoDrawer}
                        className="grid min-h-[210px] place-items-center rounded-[24px] border border-dashed border-slate-300 bg-slate-50 text-slate-600 transition hover:border-[#013E8E] hover:bg-blue-50 hover:text-[#013E8E] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-indigo-400 dark:hover:bg-indigo-500/10"
                      >
                        <span className="text-sm font-bold">
                          + Ajouter une photo
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={openRemoveDrawer}
                        className="grid min-h-[210px] place-items-center rounded-[24px] border border-dashed border-red-300 bg-red-50 text-red-700 transition hover:bg-red-100 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-500/20"
                      >
                        <span className="text-sm font-bold">
                          Retirer une photo
                        </span>
                      </button>
                    </>
                  )}
                </div>

                {visiblePhotos.length < photos.length && (
                  <div className="mt-6 flex justify-center">
                    <button
                      type="button"
                      onClick={loadMore}
                      className="rounded-2xl bg-[#013E8E] px-5 py-3 text-sm font-bold text-white transition hover:bg-indigo-800"
                    >
                      Charger plus
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      {lightboxOpen && (
        <PhotoLightbox
          photos={photos}
          startIndex={lightboxIndex}
          onClose={closeLightbox}
        />
      )}

      <PhotoDrawer
        isOpen={photoDrawerOpen}
        mode={photoDrawerMode}
        galleryId={gallery?.id}
        photo={selectedPhoto}
        onClose={closePhotoDrawer}
        onSaved={handlePhotoSaved}
      />

      <RemovePhotoDrawer
        isOpen={removeDrawerOpen}
        galleryId={gallery?.id}
        onClose={closeRemoveDrawer}
        onDeleted={handlePhotoDeleted}
      />

      <ToastContainer />
    </section>
  );
}